import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  activeTranslations,
  CHANNEL_STATUS,
  CHANNEL_STATUS_OBJECT,
  CHANNEL_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
} from '../../../../../enum/constants';
import { getIntegrationQrCode } from '../../../../../helpers/utils';
import { NotificationService } from '../../../../../services/notifications.service';
import { ChannelService } from '../../../../../services/channel.service';
import { takeUntil } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { BaseService } from '../../../../../services/base.service';
import { WebSocketService } from '../../../../../services/gateway.service';
import { ISocketIntegrationEvent, SocketIntegrationAction } from './integration.interface';
import { IntegrationService } from '../../../../../services/integration.service';
import { BaseIntegration } from '../base-integration/base-integration';

@Component({
  selector: 'app-integration-instagram',
  templateUrl: './integration-instagram.component.html',
  styleUrls: ['./integration-instagram.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationInstagramComponent extends BaseIntegration implements OnInit, OnDestroy {
  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    protected override readonly notificationService: NotificationService,
    protected override readonly channelService: ChannelService,
    protected override readonly integrationService: IntegrationService,
    protected readonly baseService: BaseService,
    protected readonly webSocketService: WebSocketService,
  ) {
    super(integrationService, notificationService, channelService);
  }

  @ViewChild('qrcode') qrCodeCanvas!: ElementRef;

  protected skipEmojiComments = false;
  protected isSkipEmojiCommentsSaving = false;

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.channel_id = this.payload.channel_id;
    this.initSockets();
    this.getChannelById();
  }

  private initSockets() {
    this.webSocketService
      .instAuthAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.handleAuthSocketActions(data);
        },
      });
  }

  protected override channelByIdCallback(): void {
    this.skipEmojiComments = !!this.channel?.x_data?.['skip_emoji_comments'];
  }

  protected onSkipEmojiCommentsChange(value: boolean): void {
    const previous = this.skipEmojiComments;
    this.skipEmojiComments = value;
    this.isSkipEmojiCommentsSaving = true;
    this.channelService
      .update(this.channel_id, { skip_emoji_comments: value })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSkipEmojiCommentsSaving = false;
        },
        error: () => {
          this.skipEmojiComments = previous;
          this.isSkipEmojiCommentsSaving = false;
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.there_was_an_error_please_try_again,
          );
        },
      });
  }

    protected connectAccount() {
    this.channelService.checkConnect({ type: CHANNEL_TYPE.INSTAGRAM }).subscribe({
      next: (data: any) => {
        if (data) {
          this.showQr = true;
          requestAnimationFrame(() => this.initQRCode());
        }
      },
    });
  }

  private initQRCode() {
    if (!this.qrCodeCanvas?.nativeElement) return;

    const qrCode = getIntegrationQrCode(this.getQrCodeData(), '/assets/svgs/integrations/instagram.svg');

    this.qrCodeCanvas.nativeElement.innerHTML = '';
    qrCode.append(this.qrCodeCanvas.nativeElement);
  }

  private getQrCodeData(): string {
    const url = new URL(environment.instagram_integration_url);
    const company = this.baseService.getCompanyObj();
    url.searchParams.set(
      'state',
      [`userId:${this.baseService.currentUserInfo.id}`, `channelId:${this.channel_id}`, `companyId:${company.id}`].join(
        '|',
      ),
    );
    this.qrUrl = String(url);

    return this.qrUrl;
  }

  private handleAuthSocketActions(event: ISocketIntegrationEvent) {
    if (this.channel_id !== event.channel_id) return;

    switch (event.action) {
      case SocketIntegrationAction.PROCESSED: {
        this.isLoader = true;
        this.showQr = false;
        break;
      }
      case SocketIntegrationAction.SUCCESS: {
        this.getChannelById();
        this.showQr = false;
        break;
      }

      case SocketIntegrationAction.FAILED: {
        this.isLoader = false;
        this.showQr = false;
        if (event.error) {
          this.notificationService.showError('', ERROR_MESSAGES[event.error] || DEFAULT_ERROR);
        }
        break;
      }
    }
  }

  protected readonly CHANNEL_STATUS = CHANNEL_STATUS;
  protected readonly CHANNEL_STATUS_OBJECT = CHANNEL_STATUS_OBJECT;
}
